import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, PackageSearch } from 'lucide-react';
import { useState } from 'react';

import type { VendorQcReturn } from '@terra/api-client';
import { Button, Spinner } from '@terra/ui';
import { RETURN_MAX_PHOTOS } from '@terra/utils';

import { api } from '../lib/api';

/** Upload one inspection photo to Cloudinary and register it; returns its URL. */
async function uploadQcPhoto(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.');
  const sig = await api.files.getUploadSignature({ folder: 'return-qc' });
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.apiKey);
  form.append('timestamp', String(sig.timestamp));
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error('The photo could not be uploaded.');
  const out = (await res.json()) as { secure_url: string };
  await api.files.registerSelf({
    name: file.name,
    url: out.secure_url,
    contentType: file.type,
    sizeBytes: file.size,
    visibility: 'PUBLIC',
  });
  return out.secure_url;
}

const errText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? (e instanceof Error ? e.message : fallback);

/** "in 1 d 4 h" / "in 3 h" / "overdue" until the inspection window closes. */
function timeLeft(deadline: string | null): { text: string; urgent: boolean } {
  if (!deadline) return { text: '', urgent: false };
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return { text: 'due now', urgent: true };
  const h = Math.floor(ms / 3_600_000);
  const d = Math.floor(h / 24);
  return { text: d > 0 ? `${d} d ${h % 24} h left` : `${Math.max(1, h)} h left`, urgent: h < 12 };
}

/**
 * Returns back with the seller, waiting for their inspection. Marking one OK
 * refunds the customer; reporting a problem (what came back, with photos)
 * holds the refund until Terra Shoppy rules. Not inspecting in time counts as
 * OK, so the customer is never left waiting.
 */
export function ReturnQcPanel() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['vendor-qc-returns'],
    queryFn: () => api.orders.vendorQcReturns(),
  });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['vendor-qc-returns'] });
    void qc.invalidateQueries({ queryKey: ['vendor-orders-returns'] });
  };

  if (list.isLoading) {
    return (
      <div className="grid min-h-[80px] place-items-center">
        <Spinner />
      </div>
    );
  }
  const rows = list.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 sm:p-5">
      <div className="flex items-center gap-2">
        <PackageSearch className="h-5 w-5 text-amber-700" />
        <h2 className="text-base font-extrabold text-amber-900">Inspect returns ({rows.length})</h2>
      </div>
      <p className="mt-1 text-xs text-amber-800">
        These came back to you. Check each one: mark it OK to refund the customer, or report a
        problem with photos. If you don&apos;t act before the deadline, the customer is refunded
        automatically.
      </p>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <QcCard key={r.id} r={r} onDone={done} />
        ))}
      </div>
    </section>
  );
}

function QcCard({ r, onDone }: { r: VendorQcReturn; onDone: () => void }) {
  const [reporting, setReporting] = useState(false);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const left = timeLeft(r.qcDeadlineAt);

  const pass = useMutation({
    mutationFn: () => api.orders.passReturnQc(r.id),
    onSuccess: onDone,
    onError: (e) => setError(errText(e, 'Could not mark it OK.')),
  });
  const fail = useMutation({
    mutationFn: () => api.orders.failReturnQc(r.id, { note: note.trim(), photoUrls: photos }),
    onSuccess: onDone,
    onError: (e) => setError(errText(e, 'Could not report the problem.')),
  });

  const addPhotos = async (files: FileList | null) => {
    if (!files) return;
    setUploading(true);
    setError(null);
    try {
      for (const f of Array.from(files).slice(0, RETURN_MAX_PHOTOS - photos.length)) {
        const url = await uploadQcPhoto(f);
        setPhotos((p) => [...p, url]);
      }
    } catch (e) {
      setError(errText(e, 'The photo could not be uploaded.'));
    } finally {
      setUploading(false);
    }
  };

  const busy = pass.isPending || fail.isPending;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-sm font-black text-[#a8161b]">
          #{r.subOrder.orderGroup.orderNumber}
        </span>
        {left.text && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
              left.urgent ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
            }`}
          >
            <Clock className="h-3.5 w-3.5" /> {left.text}
          </span>
        )}
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-800">
        {r.items
          .map((it) => `${it.lineItem.productTitle}${it.qty > 1 ? ` × ${it.qty}` : ''}`)
          .join(', ')}
      </p>
      <p className="mt-1 text-xs text-slate-600">
        <span className="font-bold">Customer&apos;s reason:</span> {r.reason}
      </p>
      {r.photoUrls.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {r.photoUrls.map((u) => (
            <a key={u} href={u} target="_blank" rel="noreferrer" title="Customer's photo">
              <img src={u} alt="" className="h-14 w-14 rounded-lg object-cover" />
            </a>
          ))}
        </div>
      )}

      {!reporting ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" loading={pass.isPending} disabled={busy} onClick={() => pass.mutate()}>
            <CheckCircle2 className="h-4 w-4" /> Item is OK: refund customer
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setReporting(true)}>
            <AlertTriangle className="h-4 w-4" /> Report a problem
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-2 rounded-lg border border-red-100 bg-red-50/50 p-3">
          <label className="block text-xs font-bold text-slate-700">
            What did you receive?
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="e.g. Box was empty / a different product / used and damaged"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal focus:border-[#a8161b] focus:outline-none"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {photos.map((u) => (
              <div key={u} className="relative">
                <img src={u} alt="" className="h-14 w-14 rounded-lg object-cover" />
                <button
                  type="button"
                  aria-label="Remove photo"
                  onClick={() => setPhotos((p) => p.filter((x) => x !== u))}
                  className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full bg-red-600 text-xs font-bold text-white"
                >
                  ×
                </button>
              </div>
            ))}
            {photos.length < RETURN_MAX_PHOTOS && (
              <label
                className={`grid h-14 w-14 cursor-pointer place-items-center rounded-lg border border-dashed border-red-300 text-[11px] font-bold text-red-700 ${uploading ? 'opacity-50' : ''}`}
              >
                {uploading ? '…' : '+ Photo'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    void addPhotos(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            Photos are required. Terra Shoppy reviews both sides before deciding the refund.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setReporting(false)}>
              Back
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={fail.isPending}
              disabled={busy || uploading || note.trim().length < 10 || photos.length === 0}
              onClick={() => fail.mutate()}
            >
              Submit problem
            </Button>
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-xs font-semibold text-red-600">{error}</p>}
    </div>
  );
}
